import {
	isValidFuelType,
	getFuelTypeNote,
	isInTransit,
	getInTransitLabel,
	highlightedAttrList,
	getVehicleStatusNote,
	getCityStateNote
} from 'wsm-highlighted-attribute';
import { highlightedAttrs } from '../global-constants';
import {
	isExistAttrConfig,
	isVehicleMakeMatchAccountFranchise
} from '../utils/utility';

/**
 * Custom hook to manage highlighted attributes logic
 * Centralizes all the conditional logic for showing different attributes
 */
export const useHighlightedAttributes = ({
	vehicle,
	highlightedAttrsConfig,
	forceShowVehicleCondition,
	labels,
	prefs,
	hasViewsData,
	numberOfViews,
	locale
}) => {
	const {
		estimatedDeliveryDate,
		status,
		certified,
		type,
		classification,
		vehicleAccountName,
		address,
		normalFuelType,
		isOffSiteVehicle,
		make
	} = vehicle || {};

	const MIN_VIEWS_THRESHOLD = 0; // Minimum views to show attribute

	// Calculate all "should show" conditions
	const shouldShowHighlightedInTransit =
		isInTransit(status) &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.ESTIMATED_DELIVERY_DATE
		);

	const conditions = {
		shouldShowHighlightedInTransit,
		shouldShowConditionAsHighlightedAttribute:
			forceShowVehicleCondition === 'false' &&
			isExistAttrConfig(
				highlightedAttrsConfig,
				highlightedAttrs.CONDITION
			),

		shouldShowOnsiteVehicle:
			!shouldShowHighlightedInTransit &&
			!isOffSiteVehicle &&
			isExistAttrConfig(
				highlightedAttrsConfig,
				highlightedAttrs.ON_SITE_VEHICLE
			),

		shouldShowSharedVehicle:
			!shouldShowHighlightedInTransit &&
			isOffSiteVehicle &&
			isExistAttrConfig(
				highlightedAttrsConfig,
				highlightedAttrs.SHARED_VEHICLE
			),

		shouldShowHighlightedFuelType:
			normalFuelType &&
			isValidFuelType(normalFuelType) &&
			isExistAttrConfig(
				highlightedAttrsConfig,
				highlightedAttrs.FUEL_TYPE
			),

		shouldShowVehicleClassification:
			isExistAttrConfig(
				highlightedAttrsConfig,
				highlightedAttrs.CLASSIFICATION
			) &&
			!!classification &&
			classification?.toLowerCase() !== 'primary',

		shouldShowVehicleViews:
			hasViewsData &&
			numberOfViews > MIN_VIEWS_THRESHOLD &&
			numberOfViews >= prefs.vehicleViews_minimumViews,

		shouldShowCityState: isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.CITY_STATE
		)
	};

	// Create attribute configurations
	const configs = [];

	// On-site vehicle
	if (conditions.shouldShowOnsiteVehicle) {
		configs.push({
			type: 'onSiteVehicle',
			content: labels.get('ON_SITE_HIGHLIGHTED_ATTRIBUTE'),
			badgeClasses: ['mr-2', 'in-stock', 'mb-2'],
			note: getVehicleStatusNote(
				highlightedAttrList.ONSITE,
				vehicleAccountName,
				labels
			)
		});
	}

	// In-transit
	if (conditions.shouldShowHighlightedInTransit) {
		configs.push({
			type: 'inTransit',
			content: getInTransitLabel({
				estimatedDeliveryDate,
				locale,
				labels
			}),
			badgeClasses: [
				'default',
				'mr-2',
				conditions.shouldShowHighlightedFuelType ? 'mb-2' : ''
			],
			note: getVehicleStatusNote(
				highlightedAttrList.ESTIMATED_DELIVERY_DATE,
				vehicleAccountName,
				labels
			)
		});
	}

	// Vehicle condition
	if (conditions.shouldShowConditionAsHighlightedAttribute) {
		let content;
		let note;

		if (certified && type === 'used') {
			content = isVehicleMakeMatchAccountFranchise(
				highlightedAttrsConfig,
				make
			)
				? labels.get('MAKE_CERTIFIED').replace(/\{\{make\}\}/, make)
				: labels.get('CERTIFIED_VEHICLE');
			note = labels.get('HIGHLIGHTED_ATTR_CERTIFIED_EXPLAIN');
		} else if (type === 'new') {
			content = labels.get('NEW');
			note = labels.get('HIGHLIGHTED_ATTR_NEW_EXPLAIN');
		} else if (type === 'used') {
			content = labels.get('USED');
			note = labels.get('HIGHLIGHTED_ATTR_USED_EXPLAIN');
		}

		if (content) {
			configs.push({
				type: 'condition',
				content,
				badgeClasses: ['mr-2', 'default', 'mb-2'],
				note
			});
		}
	}

	// Shared vehicle
	if (conditions.shouldShowSharedVehicle) {
		configs.push({
			type: 'sharedVehicle',
			content: labels.get('OFF_SITE_HIGHLIGHTED_ATTRIBUTE'),
			badgeClasses: ['mr-2', 'default', 'mb-2'],
			note: getVehicleStatusNote(
				highlightedAttrList.OFFSITE,
				vehicleAccountName,
				labels
			)
		});
	}

	// Fuel type
	if (conditions.shouldShowHighlightedFuelType) {
		configs.push({
			type: 'fuelType',
			content: normalFuelType,
			badgeClasses: [
				'default',
				'mr-2',
				conditions.shouldShowVehicleViews ? 'mb-2' : ''
			],
			note: getFuelTypeNote({ fuelType: normalFuelType, labels })
		});
	}

	// Classification
	if (conditions.shouldShowVehicleClassification) {
		const content = labels.get(classification.toUpperCase());
		if (content) {
			configs.push({
				type: 'classificationName',
				content,
				badgeClasses: ['default', 'mr-2', 'mb-2'],
				note:
					classification && type
						? labels.get(
								`HIGHLIGHTED_ATTR_${classification.toUpperCase()}_${type.toUpperCase()}_EXPLAIN`
						  )
						: ''
			});
		}
	}

	// City/State
	if (conditions.shouldShowCityState && address) {
		const { city, state } = address;
		const cityState = city && state ? `${city}, ${state}` : '';

		if (cityState) {
			configs.push({
				type: highlightedAttrList.CITY_STATE,
				content: cityState,
				badgeClasses: ['default', 'mr-2', 'mb-2'],
				note: getCityStateNote(cityState, labels, { address })
			});
		}
	}

	const attributeConfigs = configs;

	const hasAdditionalDetails = Object.values(conditions).some(Boolean);

	return {
		conditions,
		attributeConfigs,
		hasAdditionalDetails
	};
};
